import React, { FC, useCallback, useContext, useMemo } from 'react';
import Chip from '@mui/material/Chip';
import { useQueryParam } from '../../hooks/useQueryParam';
import { useLocation } from 'react-router-dom';
import styled from '@mui/styles/styled';
import { FilteringContext } from '../../contexts/FilteringContext';
import { IQuickFilter } from './QuickFilters';

const StyledChip = styled(Chip)(({ theme }) => ({
  marginLeft: theme.spacing(1),
}));

const useCheckExtra = (extra: IQuickFilter['extra']) => {
  const { search } = useLocation();

  return useMemo(() => {
    if (extra) {
      const searchObj = new URLSearchParams(search);
      return Object.entries(extra).every(([key, value]) => searchObj.get(key) === value);
    }
    return true;
  }, [search, extra]);
};

const useCheckFilter = (params: Pick<IQuickFilter, 'filterOp' | 'filterVal' | 'filterField'>) => {
  const currentFilterOp = useQueryParam('filterOp');
  const currentFilterVal = useQueryParam('filterVal');
  const currentFilterField = useQueryParam('filterField');

  return (
    (typeof params.filterField === 'undefined' &&
      typeof params.filterVal === 'undefined' &&
      typeof params.filterOp === 'undefined') ||
    (currentFilterField === params.filterField &&
      currentFilterVal === params.filterVal &&
      currentFilterOp === params.filterOp)
  );
};

export const QuickFilter: FC<IQuickFilter & { label: string }> = ({ label, ...quickFilter }) => {
  const { activateQuickFilter, deactivateQuickFilter } = useContext(FilteringContext);

  const extraMatches = useCheckExtra(quickFilter.extra);
  const filterMatches = useCheckFilter(quickFilter);

  const isActive = filterMatches && extraMatches;

  const onActivate = useCallback(() => {
    activateQuickFilter(quickFilter);
  }, [quickFilter]);

  const onDeactivate = useCallback(() => {
    deactivateQuickFilter(quickFilter);
  }, [quickFilter]);

  return (
    <StyledChip
      label={label}
      color="secondary"
      variant={isActive ? 'filled' : 'outlined'}
      onDelete={isActive ? onDeactivate : void 0}
      onClick={isActive ? void 0 : onActivate}
    />
  );
};
